/**
 * documento.js — tokens y formateadores del informe de liquidación.
 *
 * POR QUÉ NO REUSA `lib/yoolpay/formato.js`. Ése formatea para PANTALLA: `fmtClp` antepone
 * el símbolo y `fmtNum` redondea a entero. El informe es un documento impreso con su propia
 * tipografía de tabla —la moneda va en el encabezado de la columna, no en cada celda— y la
 * UF necesita conservar hasta cuatro decimales, que en pantalla nunca hicieron falta.
 *
 * LA UF SE MUESTRA CON LOS DECIMALES QUE TIENE, entre dos y cuatro. `0,404 UF` es un monto
 * real de dev y su mitad prorrateada es `0,202`: mostrarlos como «0,40» y «0,20» le esconde
 * milésimas de prima a la compañía que concilia el documento.
 *
 * @see src/components/payments/yoolpay/informe/InformeDiaDoc.jsx
 * @see src/lib/yoolpay/formato.js — el de pantalla, que este archivo NO reemplaza
 */

/** Los colores del diseño. No son los de la app: es papel, no pantalla. */
export const TINTA = {
  texto: '#1F1F1F',
  suave: '#52524F',
  tenue: '#6E6E6C',
  rotulo: '#8D8D8D',
  linea: '#DEDEDC',
  lineaFina: '#EDEDEB',
  caja: '#E4E4E2',
  marca: '#007D6E',
  marcaFondo: '#F5FBFA',
  marcaBorde: '#B7DED8',
  pie: '#9C9C9A',
};

const NUM = 'es-CL';

const esNumero = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * UF con dos decimales como piso y cuatro como techo. `null` es una raya, nunca un cero:
 * cero diría «esta fila valió 0 UF», y lo que pasa es que no está pactada en UF.
 */
export const fmtUf = (v) =>
  (esNumero(v)
    ? v.toLocaleString(NUM, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    : '—');

/**
 * CLP sin símbolo: la moneda va en el encabezado de la columna.
 *
 * Cero SÍ se muestra como cero — es un monto real («no vendió nada este día»), distinto de
 * `null`, que es «no hubo nada que informar».
 */
export const fmtClpInforme = (v) =>
  (esNumero(v) ? v.toLocaleString(NUM, { maximumFractionDigits: 0 }) : '—');

/**
 * El valor de la UF en pesos, con DOS decimales — como lo guarda `uf_diaria.valor_clp`.
 *
 * NO ES `fmtClpInforme`. Ése redondea a entero porque los montos del informe son pesos
 * cobrados, y un peso no se parte. El valor de la UF sí: la CMF lo publica con dos
 * decimales (`40.509,29`) y mostrarlo como `40.509` lo presenta como una cifra que no es
 * la publicada.
 *
 * Tampoco es `fmtUf`, que admite hasta cuatro decimales: eso es para montos EN UF
 * (`0,404 UF`), no para su valor en pesos.
 */
export const fmtValorUf = (v) =>
  (esNumero(v)
    ? v.toLocaleString(NUM, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—');

/**
 * El nombre del banco desde su código SBIF. Un código desconocido se muestra tal cual:
 * inventar un nombre en un documento con instrucciones de transferencia es peor que
 * mostrar el número.
 */
const BANCOS = {
  '001': 'Banco de Chile',
  '009': 'Banco Internacional',
  '012': 'Banco Estado',
  '014': 'Scotiabank Chile',
  '016': 'Banco BCI',
  '027': 'Corpbanca',
  '028': 'Banco Bice',
  '037': 'Banco Santander',
  '039': 'Banco Itaú',
  '049': 'Banco Security',
  '051': 'Banco Falabella',
  '053': 'Banco Ripley',
  '055': 'Banco Consorcio',
  504: 'Banco BBVA',
};

export const fmtBanco = (codigo) => BANCOS[codigo] ?? `Banco ${codigo ?? '—'}`;

/** Los tipos de cuenta que `datos_bancarios` admite. */
export const fmtTipoCuenta = (t) =>
  ({ CC: 'Cuenta corriente', CA: 'Cuenta de ahorro', CV: 'Cuenta vista' }[t] ?? t ?? '—');

const plural = (n, singular, formaPlural) => `${n} ${n === 1 ? singular : formaPlural}`;

/**
 * Por qué un día no tuvo movimiento, en las palabras que van al papel.
 *
 * NO DICE «fin de semana» ni «feriado»: en dev se cobra los siete días de la semana y el
 * PSP procesa sábados, así que ese motivo sería falso. Los días hábiles sólo mandan en la
 * fecha del giro.
 */
export function textoMotivo(motivo) {
  if (!motivo) return '';
  switch (motivo.clave) {
    case 'sin_vencimientos':
      return 'sin vencimientos';
    case 'pago_propio':
      return 'recaudó la compañía';
    case 'todo_fallido':
      return plural(motivo.fallidas, 'cuota fallida', 'cuotas fallidas');
    case 'sin_resolver':
      return plural(motivo.sin_resolver, 'cuota sin resolver', 'cuotas sin resolver');
    default:
      return 'sin movimiento';
  }
}

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** `2026-07-13` → `13-07-2026`. Sin fecha, una raya. */
export const fmtFechaDoc = (iso) => {
  if (typeof iso !== 'string' || !FECHA_RE.test(iso)) return '—';
  const [a, m, d] = iso.split('-');
  return `${d}-${m}-${a}`;
};

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const DIAS_LARGOS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** `2026-07-13` → `13 de julio de 2026`, para el título del informe del día. */
export const fmtFechaLargaDoc = (iso) => {
  if (typeof iso !== 'string' || !FECHA_RE.test(iso)) return '—';
  const [a, m, d] = iso.split('-');
  return `${Number(d)} de ${MESES[Number(m) - 1]} de ${a}`;
};

/** `2026-07-13` → `lunes`. En UTC, igual que el resto del ciclo. */
export const fmtDiaSemanaLargo = (iso) => {
  if (typeof iso !== 'string' || !FECHA_RE.test(iso)) return '';
  return DIAS_LARGOS[new Date(`${iso}T00:00:00Z`).getUTCDay()] ?? '';
};

/** `2026-07` → `Julio de 2026`, para el título del informe del mes. */
export const fmtPeriodoDoc = (period) => {
  if (typeof period !== 'string' || !/^\d{4}-\d{2}$/.test(period)) return '—';
  const [a, m] = period.split('-');
  const nombre = MESES[Number(m) - 1] ?? '';
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} de ${a}`;
};

/** Hoy en UTC, para el «emitido» del encabezado. */
export const hoyDoc = () => new Date().toISOString().slice(0, 10);
